/**
 * Error Boundary Component
 * SECURITY: Never display stack traces to users
 */

import { Component, ErrorInfo, ReactNode } from "react";
import { logError } from "@/lib/errorHandler";
import { AlertCircle, ArrowLeft } from "lucide-react";
import { Button } from "./ui/button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
  isRecoverable: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: "", isRecoverable: true };
  }

  static getDerivedStateFromError(error: Error): State {
    // Log detalhado do erro capturado
    console.error("💥 ERROR_BOUNDARY", {
      message: error?.message,
      name: error?.name,
      stack: String(error?.stack || '').slice(0, 1000),
    });
    
    const appError = logError(error, "error_boundary");
    
    // Identificar se erro é recuperável (não afeta sessão)
    const isRecoverable = 
      error.message.includes('Cannot read properties') ||
      error.message.includes('undefined') ||
      error.message.includes('null') ||
      !error.message.includes('Network') ||
      !error.message.includes('Auth');
    
    return {
      hasError: true,
      errorMessage: appError.userMessage,
      isRecoverable,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to monitoring service in production
    logError(
      {
        error: error.message,
        componentStack: errorInfo.componentStack,
      },
      "component_error"
    );
  }

  handleGoBack = () => {
    this.setState({ hasError: false, errorMessage: "", isRecoverable: true });
    window.history.back();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-br from-amber-500 to-amber-600">
          <div className="text-center space-y-4 max-w-md bg-white dark:bg-gray-900 rounded-lg p-8 shadow-2xl">
            <AlertCircle className="w-16 h-16 text-amber-600 mx-auto" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Algo deu errado
            </h1>
            <p className="text-gray-600 dark:text-gray-400">{this.state.errorMessage}</p>
            
            {this.state.isRecoverable && (
              <div className="flex items-center justify-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-md">
                <span className="text-green-700 dark:text-green-400 text-sm font-medium">
                  ✅ Sua sessão foi mantida
                </span>
              </div>
            )}
            
            <div className="flex justify-center">
              <Button 
                onClick={this.handleGoBack} 
                className="bg-amber-600 hover:bg-amber-700 text-white w-full max-w-xs"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar à página anterior
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
